import { Outlet, createFileRoute, redirect } from '@tanstack/react-router'
import { DashboardProvider } from '@/contexts/dashboard-context'
import { getDashboard, getDashboards } from '@/api/workspace/workspace.actions'
import { clearTokens } from '@/lib/token-storage'
import { redirectToLoginApp } from '@/lib/login-app-url'

// Layout route para /dashboard/$dashboardId
// Esta rota serve como layout intermediário que envolve as rotas filhas
// O conteúdo específico de /dashboard/$dashboardId está em dashboard.$dashboardId.index.tsx
// Rotas filhas como /dashboard/$dashboardId/contatos serão renderizadas no <Outlet />
// Hierarquia: dashboard.tsx → dashboard.$dashboardId.tsx (este) → rotas filhas
export const Route = createFileRoute('/(dashboard)/dashboard/$dashboardId')({
  beforeLoad: async ({ params, location }) => {
    const dashboardId = params.dashboardId

    // Valida se o dashboardId é válido (número)
    const dashboardIdNum = parseInt(dashboardId, 10)
    if (isNaN(dashboardIdNum)) {
      // Se não for um número válido, redireciona para workspace
      throw redirect({
        to: '/workspace',
      })
    }

    // Verifica se o usuário tem acesso a esta dashboard
    try {
      const dashboardsResponse = await getDashboards()

      if (dashboardsResponse.success && dashboardsResponse.data) {
        // Verifica se o dashboardId está na lista de dashboards disponíveis
        const hasAccess = dashboardsResponse.data.some(
          (dashboard) => dashboard.id === dashboardIdNum,
        )

        if (!hasAccess) {
          // Usuário não tem acesso a esta dashboard, redireciona para workspace
          throw redirect({
            to: '/workspace',
          })
        }

        // Verifica se o trial expirou e redireciona para página de assinatura
        // Exceto se já estiver na página de assinatura
        const isAssinarPage = location.pathname.includes('/assinar')
        
        if (!isAssinarPage) {
          try {
            const dashboardData = await getDashboard(dashboardId)
            const dashboardStatus = dashboardData.data.status

            // Se status for Pendente, redireciona para página de assinatura
            if (dashboardStatus === 'Pendente') {
              throw redirect({
                to: '/dashboard/$dashboardId/assinar',
                params: { dashboardId },
              })
            }

            // Se estiver em trial e o trial expirou, redireciona para página de assinatura
            if (dashboardData.data.trial && dashboardData.data.trialExpiration) {
              const expirationDate = new Date(dashboardData.data.trialExpiration)
              const now = new Date()
              
              if (expirationDate <= now) {
                // Trial expirado - redireciona para página de assinatura
                throw redirect({
                  to: '/dashboard/$dashboardId/assinar',
                  params: { dashboardId },
                })
              }
            }
          } catch (redirectError: any) {
            // Se for um redirect, propaga
            if (redirectError?.to) {
              throw redirectError
            }
            // Se for outro erro (ex.: 307, rede), ignora e não bloqueia acesso
          }
        }
      } else {
        // Resposta não foi bem-sucedida, redireciona para workspace
        throw redirect({
          to: '/workspace',
        })
      }
    } catch (error: any) {
      // Se for erro 401 (não autorizado), significa que:
      // - O apiClient já tentou renovar o token automaticamente com refreshToken
      // - Se o refresh falhou, o apiClient já limpou os tokens
      // - Redireciona para login para que o usuário faça login novamente
      if (error?.statusCode === 401 || error?.status === 401) {
        // Garante que os tokens estão limpos (caso o apiClient não tenha limpado)
        clearTokens()
        // Redireciona para login
        throw redirectToLoginApp()
      }

      // Se for erro 404 (dashboard não encontrada), redireciona para workspace
      if (error?.statusCode === 404 || error?.status === 404) {
        throw redirect({
          to: '/workspace',
        })
      }

      // Erro interno temporário — não invalidar sessão; volta ao workspace
      if (error?.statusCode === 500 || error?.status === 500) {
        throw redirect({
          to: '/workspace',
        })
      }

      // Para outros erros, limpa tokens e redireciona para login por segurança
      clearTokens()
      throw redirectToLoginApp()
    }
  },
  component: function DashboardIdLayout() {
    return (
      <DashboardProvider>
        {/* Layout intermediário - renderiza todas as rotas filhas dentro dele
            Isso garante que dashboard.$dashboardId.contatos.tsx seja filho deste layout
            e dashboard.$dashboardId.contatos.$id.tsx seja filho do layout de contatos
            
            O DashboardProvider envolve todas as rotas filhas, permitindo que qualquer
            componente acesse o dashboardId através do hook useDashboard() sem precisar
            passar como prop. O provider lê diretamente da URL, então sempre está atualizado.
        */}
        <Outlet />
      </DashboardProvider>
    )
  },
})
